import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Building2, Users, Calendar, Award, Target, Eye, Heart, Lightbulb, 
  ArrowRight, Shield, Handshake, CheckCircle, Star, TrendingUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { COMPANY, ABOUT_PAGE, BRAND_PARTNERS, HOME_PAGE } from "../config/content";
import Marquee from "react-fast-marquee";
import Header from "./Header";
import Footer from "./Footer";

const valueIcons = {
  "Transparency": Eye,
  "Honesty": Heart,
  "Quality of Service": Star,
  "People-Centric Approach": Users,
  "Integrity": Shield
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="overline text-[#0047FF] mb-4">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
              {ABOUT_PAGE.hero.title}
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto font-medium">
              {ABOUT_PAGE.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-[#0047FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            {HOME_PAGE.stats.map((stat, index) => (
              <div key={index}>
                <p className="text-3xl md:text-4xl font-black font-heading">{stat.number}</p>
                <p className="text-sm opacity-80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="overline text-[#0047FF] mb-4">{ABOUT_PAGE.whoWeAre.title}</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Building Trust Since {COMPANY.established}
              </h2>
              {ABOUT_PAGE.whoWeAre.paragraphs.map((para, index) => (
                <p key={index} className="text-slate-600 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
              
              {/* Founders */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                {ABOUT_PAGE.founders.map((founder, index) => (
                  <div key={index} className="bg-slate-50 p-4 border border-slate-200">
                    <p className="font-semibold text-slate-900">{founder.name}</p>
                    <p className="text-sm text-[#0047FF]">{founder.role}</p>
                    <p className="text-xs text-slate-500 mt-1">{founder.expertise}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={ABOUT_PAGE.teamSection.image}
                alt="Our Team"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commitment to Quality */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <CheckCircle className="w-12 h-12 text-[#0047FF] mx-auto mb-6" />
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            {ABOUT_PAGE.commitment.title}
          </h2>
          <p className="text-slate-600 leading-relaxed text-lg">
            {ABOUT_PAGE.commitment.content}
          </p>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline text-[#0047FF] mb-4">Our Principles</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Our Core Values
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              At Shree Siddhivinayak Enterprise, our business philosophy is built on strong ethical foundations and a commitment to long-term partnerships.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ABOUT_PAGE.coreValues.map((value, index) => {
              const Icon = valueIcons[value.title] || Award;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-slate-50 border border-slate-200 p-8 hover:border-[#0047FF] transition-colors"
                >
                  <div className="w-14 h-14 bg-[#0047FF] text-white flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey Timeline Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline text-[#0047FF] mb-4">Our History</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
              Journey of Shree Siddhivinayak Enterprise
            </h2>
          </div>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#0047FF]"></div>
            
            {/* Timeline Items */}
            <div className="space-y-12">
              {ABOUT_PAGE.journey.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-[#0047FF] rounded-full transform -translate-x-1/2 z-10 border-4 border-slate-900"></div>
                  
                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'
                  }`}>
                    <div className="bg-slate-800 p-6 border border-slate-700 hover:border-[#0047FF] transition-colors">
                      <span className="inline-block bg-[#0047FF] text-white text-sm font-bold px-3 py-1 mb-3">
                        {item.year}
                      </span>
                      <h3 className="font-heading text-xl font-semibold mb-2">
                        {item.title}
                      </h3>
                      <p className="text-slate-400 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Future Goal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-3 bg-[#0047FF] px-8 py-4">
              <TrendingUp className="w-6 h-6" />
              <span className="font-heading text-xl font-bold">Target: ₹100 Crore by 2030</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand Partners */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="overline text-center">Our Strategic Partners</p>
        </div>
        <Marquee gradient={false} speed={40} pauseOnHover={true}>
          <div className="flex items-center gap-12 md:gap-16 px-8">
            {BRAND_PARTNERS.map((brand) => (
              <a 
                key={brand.name}
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center h-16 md:h-20 min-w-[120px] md:min-w-[160px] grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
              >
                <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-[140px] md:max-w-[180px] object-contain" loading="lazy" />
              </a>
            ))}
          </div>
        </Marquee>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0047FF]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Contact us today to discuss your laboratory equipment needs.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#0047FF] hover:bg-slate-100 rounded-none px-8 py-6 text-base">
              Get In Touch <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
